import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/Button/Button';

import { formatRelativeAnnotationTime } from './annotationTime';
import { getAnnotationDisplayLabel, type Annotation } from './annotationTypes';
import styles from './AnnotationCard.module.css';

interface AnnotationCardProps {
  annotation: Annotation;
  isOrphaned: boolean;
  onDelete: (annotation: Annotation) => Promise<void> | void;
  onSelect: () => void;
}

export function AnnotationCard({
  annotation,
  isOrphaned,
  onDelete,
  onSelect,
}: AnnotationCardProps) {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  if (isConfirmingDelete) {
    return (
      <div className={styles.card}>
        <p className={styles.confirmationTitle}>
          Delete this {getAnnotationDisplayLabel(annotation).toLowerCase()}?
        </p>
        <div className={styles.confirmationActions}>
          <Button
            onClick={() => setIsConfirmingDelete(false)}
            size="small"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            destructive
            onClick={() => {
              void onDelete(annotation);
            }}
            size="small"
            variant="secondary"
          >
            Delete
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card} data-orphaned={isOrphaned ? 'true' : 'false'}>
      <button className={styles.body} onClick={onSelect} type="button">
        <blockquote className={styles.quote}>
          {annotation.quote.exact || 'Untitled selection'}
        </blockquote>
        {annotation.note ? <p className={styles.note}>{annotation.note}</p> : null}
        {isOrphaned ? (
          <p className={styles.orphanStatus}>
            This annotation could not be matched in the current text.
          </p>
        ) : null}
        <div className={styles.meta}>
          <span className={styles.kind}>
            {getAnnotationDisplayLabel(annotation)}
          </span>
          <span className={styles.timestamp}>
            {formatRelativeAnnotationTime(annotation.timestamp)}
          </span>
        </div>
      </button>

      <button
        aria-label={`Delete ${getAnnotationDisplayLabel(annotation).toLowerCase()}`}
        className={styles.deleteButton}
        onClick={() => setIsConfirmingDelete(true)}
        type="button"
      >
        <Trash2 aria-hidden="true" size={14} />
      </button>
    </div>
  );
}
